<template>
  <ProvisionedRepair
    v-if="needsRepair"
    class="team-pages-repair"
    :org-id="orgId"
    :repairing="isRepairing"
    :failed="repairFailed"
    @repair="repair"
  />
  <PagesSplitView v-else class="team-pages">
    <template
      v-if="currentSection && currentSection.content && canCreate"
      #header-right
    >
      <BaseButton
        class="add"
        kind="auxiliary"
        @click="addPage(currentSection.workspaceId)"
      >
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </template>
    <template v-if="currentSection && workspaceOptions.length > 1" #toolbar>
      <div class="workspace-toolbar">
        <BaseInputSelect
          :key="`workspace-select-${selectRefresh}`"
          class="workspace-select"
          :value="currentSection.workspaceId"
          :options="workspaceOptions"
          select-only
          @selected="selectWorkspace($event.value)"
        >
          <template #item="{ option }">
            <BaseInputSelectOptionItem :title="option.label">
              <template v-if="!isWorkspaceAccessible(option.value)" #right>
                <LockIcon class="lock-icon" />
              </template>
            </BaseInputSelectOptionItem>
          </template>
        </BaseInputSelect>
      </div>
    </template>
    <template #list>
      <PagesForestPane
        v-if="currentPaneSource"
        :key="currentSection?.workspaceId"
        :source="currentPaneSource"
        :writable="canCreate"
      />
      <div v-else class="no-pages-database">
        <span>Create a page, give it a title and start taking notes.</span>
      </div>
    </template>
    <template #preview="{ pagesPaneWidth }">
      <Suspense v-if="selected">
        <ObjectComponent
          :id="selected.rowId"
          :key="selected.rowId"
          :workspace-id="selected.workspaceId"
          :database-id="selected.databaseId"
          :left-panel-width="pagesPaneWidth"
          :autofocus-title="selected.rowId === justCreatedRowId"
          show-open-action
        />
        <template #fallback>
          <PageLoading />
        </template>
      </Suspense>
      <EmptyPagesPreview v-else />
    </template>
  </PagesSplitView>
</template>

<script lang="ts">
import { computed, defineComponent, ref, shallowRef, watch } from "vue";

import type {
  GraphDatabase,
  ObjectIdRaw,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseInputSelectOptionItem from "@/components/base/BaseInputSelectOptionItem.vue";
import ObjectComponent from "@/components/graph/Object.vue";
import { asGraphRecordResult, asString } from "@/components/graph/result";
import { extractTitleFromGraphResult } from "@/components/graph/title";
import { getRoutineParentId } from "@/components/graph/utils";
import AddIcon from "@/components/icons/AddIcon.vue";
import LockIcon from "@/components/icons/LockIcon.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import PageLoading from "@/components/PageLoading.vue";
import EmptyPagesPreview from "@/components/pages/EmptyPagesPreview.vue";
import type { PagesForestSource } from "@/components/pages/pagesForest";
import PagesForestPane from "@/components/pages/PagesForestPane.vue";
import PagesSplitView from "@/components/pages/PagesSplitView.vue";
import {
  createTeamPagesSource,
  type TeamPageRow,
} from "@/components/pages/teamPagesSource";
import ProvisionedRepair from "@/components/ProvisionedRepair.vue";
import {
  getOrganizationDefaultWorkspace,
  organizationWorkspaceSelectionStorageKey,
  resolveExistingPagesDatabase,
  WORKSPACES_ENTITLEMENT_KEY,
} from "@/components/workspaces";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { pagesOpenedStorageKey } from "@/composables/usePages";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { useProvisionedRepair } from "@/composables/useProvisionedRepair";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { isWellFormedObjectId } from "@/utils/id";
import { mapIfDefined } from "@/utils/undefined";

// One left-pane section per accessible workspace; `content` is absent when
// the workspace has no pages database (e.g. the user deleted the Page type)
// — the screen then shows a plain empty message and the Repair flow can
// recreate the type.
type PagesSection = {
  workspaceId: WorkspaceId;
  workspaceName: string;
  content?: { database: GraphDatabase };
};

/**
 * Team-scoped Pages screen, sharing the personal Pages experience: the left
 * pane is the shared `PagesForestPane` (nested drag & drop with Escape cancel,
 * manual ordering, collapse state, selection with neighbor reselection) over
 * a `createTeamPagesSource` per workspace. Team pages are graph objects of
 * each workspace's Page type (they don't live in the CRDT notes hierarchy
 * like personal pages), so the source derives its forest from the live rows
 * query of the workspace's pages database, and the editor is the custom
 * object view.
 */
export default defineComponent({
  name: "TeamPages",
  components: {
    AddIcon,
    BaseButton,
    BaseInputSelect,
    BaseInputSelectOptionItem,
    EmptyPagesPreview,
    LockIcon,
    ObjectComponent,
    PageLoading,
    PagesSplitView,
    PagesForestPane,
    ProvisionedRepair,
  },
  props: {
    orgId: { type: String, required: true },
    // Selected node: a row of one of the org's pages databases, or the full
    // graph object id of a host object previewed from the forest.
    rowId: { type: String, required: false },
  },
  async setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const profile = inject.profile();
    const billing = inject.billing();
    const contextMenu = inject.contextMenu();
    const baseLogger = inject.logger();
    const logger = baseLogger.forModule("TeamPages");
    const releaser = useReleaser();

    const { get, syncState } = useBrowserStore(profile.value.user.accountId);
    // The organization's last open page, restored when coming back to the
    // screen or switching workspaces.
    const lastOpenPage = releaser.add(
      syncState<string | null>(pagesOpenedStorageKey(props.orgId), null)
    );
    const defaultSelectedWorkspaceId = get(
      `pages:org:${props.orgId}:workspace`,
      ""
    );
    const selectedWorkspaceId = releaser.add(
      syncState<string>(
        organizationWorkspaceSelectionStorageKey(props.orgId),
        defaultSelectedWorkspaceId
      )
    );

    // Ordered ids of every workspace in the organization, default first.
    // The screen itself is always reachable: additional workspaces stay
    // listed in the selector but are locked without the "workspaces"
    // entitlement (same rule as TeamTasks / Projects).
    const resolveOrderedWorkspaceIds = async (): Promise<WorkspaceId[]> => {
      const defaultWorkspaceId = await getOrganizationDefaultWorkspace(
        controller,
        props.orgId
      );
      const workspaceIds =
        profile.value.organizations
          .find((org) => org.id === props.orgId)
          ?.workspaces.map((workspace) => workspace.id) ?? [];
      return defaultWorkspaceId
        ? [
            defaultWorkspaceId,
            ...workspaceIds.filter((id) => id !== defaultWorkspaceId),
          ]
        : workspaceIds;
    };

    const orderedWorkspaceIds = await resolveOrderedWorkspaceIds();
    const defaultWorkspaceId = orderedWorkspaceIds[0];
    const isEntitledToWorkspaces = computed<boolean>(
      () =>
        billing.value?.isEntitledTo(WORKSPACES_ENTITLEMENT_KEY, props.orgId)
          ?.value ?? false
    );
    const isWorkspaceAccessible = (workspaceId: string): boolean =>
      isEntitledToWorkspaces.value || workspaceId === defaultWorkspaceId;
    if (!isWorkspaceAccessible(selectedWorkspaceId.value) && defaultWorkspaceId)
      selectedWorkspaceId.value = defaultWorkspaceId;

    // Compile and run the live rows query of one pages database (reactive:
    // the ref updates when rows are added, renamed, re-parented or removed).
    const watchPagesDatabase = async (database: GraphDatabase) => {
      const compiledRef = await controller.graph.query.compile(
        database.workspace,
        `databases.["${database.id}"].rows`,
        {}
      );
      const compiled = compiledRef.release();
      if (!compiled || "error" in compiled) {
        logger.error("Unable to compile the pages query", { compiled });
        return undefined;
      }
      const rowsRef = await releaser.add(
        controller.graph.query.run(compiled.value)
      );
      return { database, rowsRef };
    };

    const databasesRef = await controller.graph.database.list();
    const databasesByWorkspace = databasesRef.release();
    const initialSources = (
      await Promise.all(
        orderedWorkspaceIds.map(async (workspaceId) => {
          const database = await resolveExistingPagesDatabase(
            controller,
            props.orgId,
            databasesByWorkspace?.[workspaceId] ?? {}
          );
          return database ? watchPagesDatabase(database) : undefined;
        })
      )
    ).filter(
      (source): source is NonNullable<typeof source> => source !== undefined
    );

    // One live query per workspace that has a pages database; grows when a
    // missing Page type is restored from the empty state's Repair button.
    const sources = shallowRef(initialSources);

    const rawSections = computed(() =>
      sources.value.map(({ database, rowsRef }) => {
        const rows: TeamPageRow[] = [];
        const result = rowsRef.value;
        if (result && !("error" in result) && result.value instanceof Map) {
          for (const [key, raw] of result.value.entries()) {
            const record = asGraphRecordResult(raw);
            if (!record) continue;
            const rowId =
              mapIfDefined(asString)(
                mapIfDefined(asGraphRecordResult)(record._routine)?.id
              ) ?? (typeof key === "string" ? key : undefined);
            if (!rowId) continue;
            rows.push({
              rowId,
              title: extractTitleFromGraphResult(record) ?? "",
              parentId: getRoutineParentId(raw),
            });
          }
        }
        return { database, rows };
      })
    );

    // `_routine.parent` values are opaque object-id strings; decompose them
    // through the controller once and cache the result (null when parsing
    // fails) so the forests can be derived synchronously. The forest sources
    // also feed host objects' own pointers through `requestParse`.
    const parsedParents = ref<Record<string, ObjectIdRaw | null>>({});
    const parentsBeingParsed = new Set<string>();
    const requestParse = (raw: string) => {
      if (raw in parsedParents.value || parentsBeingParsed.has(raw)) return;
      parentsBeingParsed.add(raw);
      controller.graph.object.id.parse(raw).then(
        (parsed) => (parsedParents.value[raw] = parsed),
        () => (parsedParents.value[raw] = null)
      );
    };
    const stopParentParsing = watch(
      rawSections,
      (sectionRows) => {
        for (const { rows } of sectionRows)
          for (const { parentId } of rows) if (parentId) requestParse(parentId);
      },
      { immediate: true }
    );
    releaser.defer(stopParentParsing);

    // Row id of the page just created from a "+" button: the object editor
    // focuses its (empty) title when it opens. Cleared as soon as another
    // page is opened so revisiting the page doesn't steal the caret.
    const justCreatedRowId = ref<string | null>(null);
    const stopJustCreatedWatch = watch(
      () => props.rowId,
      (rowId) => {
        if (justCreatedRowId.value && rowId !== justCreatedRowId.value)
          justCreatedRowId.value = null;
      }
    );
    releaser.defer(stopJustCreatedWatch);

    // One `PagesForestSource` per workspace that has a pages database, created
    // lazily and kept for the screen's lifetime (they own live caches). The
    // pane is re-keyed per workspace, so only the current section's source
    // is mounted.
    const paneSources = new Map<WorkspaceId, PagesForestSource>();
    const paneSourceOf = (database: GraphDatabase): PagesForestSource => {
      const existing = paneSources.get(database.workspace);
      if (existing) return existing;
      const source = createTeamPagesSource({
        controller,
        baseLogger,
        router,
        contextMenu,
        orgId: props.orgId,
        database,
        rows: computed(
          () =>
            rawSections.value.find(
              (section) => section.database.workspace === database.workspace
            )?.rows ?? []
        ),
        parsedParents,
        requestParse,
        own: (stop) => releaser.defer(stop),
        recordOpened: (rowId) => {
          lastOpenPage.value = rowId;
        },
        markJustCreated: (rowId) => {
          justCreatedRowId.value = rowId;
        },
      });
      paneSources.set(database.workspace, source);
      return source;
    };

    const sections = computed<PagesSection[]>(() => {
      const workspaces =
        profile.value.organizations.find((org) => org.id === props.orgId)
          ?.workspaces ?? [];
      const sourceByWorkspace = new Map(
        rawSections.value.map((section) => [
          section.database.workspace,
          section,
        ])
      );
      return orderedWorkspaceIds.map((workspaceId) => {
        const source = sourceByWorkspace.get(workspaceId);
        return {
          workspaceId,
          workspaceName:
            workspaces.find((workspace) => workspace.id === workspaceId)
              ?.name ?? "Workspace",
          content: source ? { database: source.database } : undefined,
        };
      });
    });
    const currentSection = computed<PagesSection | undefined>(() => {
      const selected = sections.value.find(
        (section) =>
          section.workspaceId === selectedWorkspaceId.value &&
          isWorkspaceAccessible(section.workspaceId)
      );
      if (selected) return selected;
      return (
        sections.value.find((section) =>
          isWorkspaceAccessible(section.workspaceId)
        ) ?? sections.value[0]
      );
    });
    const currentPaneSource = computed<PagesForestSource | undefined>(() => {
      const database = currentSection.value?.content?.database;
      return database ? paneSourceOf(database) : undefined;
    });
    const workspaceOptions = computed<SelectOption[]>(() => {
      const workspaces =
        profile.value.organizations.find((org) => org.id === props.orgId)
          ?.workspaces ?? [];
      return [...workspaces]
        .sort((a, b) => {
          if (a.id === defaultWorkspaceId) return -1;
          if (b.id === defaultWorkspaceId) return 1;
          return a.name.localeCompare(b.name);
        })
        .map((workspace) => ({
          value: workspace.id,
          label: workspace.name,
          aliases: [],
        }));
    });
    const selectRefresh = ref(0);
    let isSelectingWorkspace = false;
    const selectWorkspace = (workspaceId: string) => {
      if (isWorkspaceAccessible(workspaceId)) {
        isSelectingWorkspace = true;
        selectedWorkspaceId.value = workspaceId;
        const workspaceRows = allRows.value.filter(
          (row) => row.database.workspace === workspaceId
        );
        const target =
          workspaceRows.find((row) => row.rowId === lastOpenPage.value) ??
          workspaceRows[0];
        const route = target
          ? {
              name: RouteNames.OrgPages,
              params: { orgId: props.orgId },
              query: { id: target.rowId },
            }
          : {
              name: RouteNames.OrgPages,
              params: { orgId: props.orgId },
            };
        void router.replace(route).finally(() => {
          isSelectingWorkspace = false;
        });
      } else {
        openUpgradeModal(billing, props.orgId, WORKSPACES_ENTITLEMENT_KEY);
        selectRefresh.value += 1;
      }
    };

    // Every known page row (visible or collapsed), with its database.
    const allRows = computed(() =>
      rawSections.value.flatMap(({ database, rows }) =>
        rows.map((row) => ({ rowId: row.rowId, database }))
      )
    );
    // The selected node and the coordinates that drive the editor: a page
    // row of an accessible workspace, or a host object previewed from the
    // forest (its `id` query is a full graph object id, parsed below). Pages
    // in a locked workspace are treated as unselected until the selection
    // watch replaces the route with an accessible page.
    const hostTriple = ref<{
      workspaceId: WorkspaceId;
      databaseId: string;
      rowId: string;
    } | null>(null);
    const stopHostParsing = watch(
      () => props.rowId,
      async (id) => {
        if (!id || !isWellFormedObjectId(id)) {
          hostTriple.value = null;
          return;
        }
        const raw = await controller.graph.object.id.parse(id);
        // A navigation may have raced the parse.
        if (props.rowId !== id) return;
        hostTriple.value =
          raw.workspace_id && raw.table_id && raw.row_id
            ? {
                workspaceId: raw.workspace_id,
                databaseId: raw.table_id,
                rowId: raw.row_id,
              }
            : null;
      },
      { immediate: true }
    );
    releaser.defer(stopHostParsing);
    const selected = computed(() => {
      const rowId = props.rowId;
      if (!rowId) return undefined;
      const entry = allRows.value.find((row) => row.rowId === rowId);
      if (entry && isWorkspaceAccessible(entry.database.workspace))
        return {
          rowId,
          workspaceId: entry.database.workspace,
          databaseId: entry.database.id,
        };
      // A page created from a "+" navigates here before the live rows query
      // ticks with it: its coordinates are the current section's database
      // (where `createPage` inserted it). Mounting the editor right away —
      // the row itself is already stored — instead of flashing the empty
      // preview and remounting on the tick.
      const justCreatedIn = currentSection.value?.content?.database;
      if (
        rowId === justCreatedRowId.value &&
        justCreatedIn &&
        isWorkspaceAccessible(justCreatedIn.workspace)
      )
        return {
          rowId,
          workspaceId: justCreatedIn.workspace,
          databaseId: justCreatedIn.id,
        };
      const host = hostTriple.value;
      if (host && isWorkspaceAccessible(host.workspaceId))
        return {
          rowId: host.rowId,
          workspaceId: host.workspaceId,
          databaseId: host.databaseId,
        };
      return undefined;
    });

    // A `reader` member of the displayed workspace cannot create pages: the
    // header "+" and the forest's add buttons (empty-state "New", the rows'
    // hover "+" for subpages) are hidden.
    const canCreate = computed(() =>
      isWorkspaceWritable(profile.value, currentSection.value?.workspaceId)
    );

    // The header "+": create a page at the current workspace's root.
    // Creation never provisions: without a pages database the pane shows a
    // plain empty message instead (the Repair flow restores the type).
    const addPage = async (workspaceId: WorkspaceId) => {
      if (!canCreate.value) return;
      const database = sources.value.find(
        (candidate) => candidate.database.workspace === workspaceId
      )?.database;
      if (!database) return;
      await paneSourceOf(database).createPage(null);
    };

    // Inspect the provisioned types of all the organization's workspaces:
    // when any is broken the whole screen becomes a Repair empty state,
    // even if pages themselves are unaffected. After a repair, the listed
    // workspaces that had no pages database get their restored Page rows
    // watched as new sources.
    const { needsRepair, isRepairing, repairFailed, repair } =
      useProvisionedRepair(
        controller,
        profile,
        () =>
          profile.value.organizations
            .find((org) => org.id === props.orgId)
            ?.workspaces.map((workspace) => workspace.id) ?? [],
        async () => {
          const repairedRef = await controller.graph.database.list();
          const repairedByWorkspace = repairedRef.release();
          for (const workspaceId of orderedWorkspaceIds) {
            if (
              sources.value.some(
                (source) => source.database.workspace === workspaceId
              )
            )
              continue;
            const database = await resolveExistingPagesDatabase(
              controller,
              props.orgId,
              repairedByWorkspace?.[workspaceId] ?? {}
            );
            if (!database) continue;
            const source = await watchPagesDatabase(database);
            if (source) sources.value = [...sources.value, source];
          }
        }
      );

    // Keep the selection valid: without an explicit selection, open the last
    // opened page (if it still exists) or the first one; when the selected
    // page is deleted, fall back the same way (the pane's own
    // neighbor-reselection, running after this watch, refines the target
    // when the deletion happened on a selected node). Host object previews
    // (`id` = a full object id) are left alone. Deep links into a locked
    // workspace fall back to the default workspace without opening the
    // upgrade modal — only an explicit selector choice offers the upgrade.
    const stopSelectionWatch = watch(
      [allRows, currentSection, () => props.rowId] as const,
      ([rows, section, rowId], old) => {
        if (!isSelectingWorkspace) {
          if (rowId && isWellFormedObjectId(rowId)) return;
          const routeEntry = rowId
            ? rows.find((row) => row.rowId === rowId)
            : undefined;
          if (
            routeEntry &&
            isWorkspaceAccessible(routeEntry.database.workspace)
          ) {
            if (routeEntry.database.workspace !== selectedWorkspaceId.value) {
              selectedWorkspaceId.value = routeEntry.database.workspace;
            }
          } else {
            if (
              routeEntry &&
              !isWorkspaceAccessible(routeEntry.database.workspace) &&
              defaultWorkspaceId &&
              selectedWorkspaceId.value !== defaultWorkspaceId
            ) {
              selectedWorkspaceId.value = defaultWorkspaceId;
            }
            const currentRows = section
              ? rows.filter(
                  (row) => row.database.workspace === section.workspaceId
                )
              : [];
            const previousRows = old?.[0]?.filter(
              (row) => row.database.workspace === section?.workspaceId
            );
            const has = (list: typeof rows, id: string) =>
              list.some((row) => row.rowId === id);
            if (rowId) {
              // A freshly created page can reach the route before the live
              // query: only treat the row as deleted when it was known before.
              // Deep links into a locked workspace also look "missing" from
              // the accessible section — fall through to replace them.
              if (
                previousRows &&
                !has(previousRows, rowId) &&
                !(
                  routeEntry &&
                  !isWorkspaceAccessible(routeEntry.database.workspace)
                )
              )
                return;
            }
            const target =
              currentRows.find((row) => row.rowId === lastOpenPage.value) ??
              currentRows[0];
            if (!target) {
              if (rowId)
                void router.replace({
                  name: RouteNames.OrgPages,
                  params: { orgId: props.orgId },
                });
              return;
            }
            if (target.rowId === rowId) return;
            void router.replace({
              name: RouteNames.OrgPages,
              params: { orgId: props.orgId },
              query: { id: target.rowId },
            });
          }
        }
      },
      { immediate: true }
    );
    releaser.defer(stopSelectionWatch);

    return {
      addPage,
      canCreate,
      currentPaneSource,
      currentSection,
      isRepairing,
      isWorkspaceAccessible,
      justCreatedRowId,
      needsRepair,
      repair,
      repairFailed,
      selectRefresh,
      selectWorkspace,
      selected,
      workspaceOptions,
    };
  },
});
</script>

<style scoped lang="scss">
.team-pages {
  // Match TitledDocument's side gutters so editor block controls are not
  // clipped against the edge of the split pane.
  :deep(.object > .object-content) {
    width: calc(100% - 192px);
    padding-inline-end: 96px;
    padding-inline-start: 96px;
  }
}

.workspace-toolbar {
  display: flex;
  align-items: center;
  column-gap: 4px;
  padding: 8px 8px 0;

  .workspace-select {
    flex: 1;
    min-width: 0;

    .lock-icon {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
      :deep(.stroke) {
        stroke: $red;
      }
    }
  }
}

// Rendered in the left pane header (BasePaneHeader's "right" slot).
button.base-button.auxiliary.icon-only.add {
  flex-shrink: 0;
  padding: 4px;
}

// Flex item of Routine's #content row: grow to fill the whole pane, like
// PagesSplitView does, so the message centers in the screen.
.team-pages-repair {
  flex: 1;
  height: 100%;
}

// A workspace without a pages database: a plain message (the "+" is hidden;
// the Repair flow recreates the Page type).
.no-pages-database {
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  align-items: center;
  justify-content: center;
  height: calc(100% - 64px);

  span {
    text-align: center;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-50);
    }
  }
}
</style>
